import { jsx as _jsx } from "react/jsx-runtime";
import noop from "lodash/noop";
import omit from "lodash/omit";
import Router from "next/router";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { QueryParamNames } from "~/constants/search";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
export var AssetModalPanel;
(function(AssetModalPanel) {
    AssetModalPanel["ANNOTATIONS"] = "ANNOTATIONS";
    AssetModalPanel["BOARDS"] = "BOARDS";
    AssetModalPanel["CAPTURED_TEXT"] = "CAPTURED_TEXT";
    AssetModalPanel["CHAPTERS"] = "CHAPTERS";
    AssetModalPanel["COMMENTS"] = "COMMENTS";
    AssetModalPanel["FIELDS"] = "FIELDS";
    AssetModalPanel["INFO"] = "INFO";
    AssetModalPanel["NONE"] = "NONE";
    AssetModalPanel["TRANSCRIPT"] = "TRANSCRIPT";
    AssetModalPanel["VERSIONS"] = "VERSIONS";
    AssetModalPanel["PEOPLE"] = "PEOPLE";
})(AssetModalPanel || (AssetModalPanel = {}));
const defaultContextValue = {
    panel: "NONE",
    hidePanel: noop,
    showPanel: noop,
    togglePanel: noop,
    updatePanel: noop
};
export const AssetModalPanelContext = /*#__PURE__*/ createContext(defaultContextValue);
export const AssetModalPanelContextProvider = (param)=>{
    let { children } = param;
    const [panel, setPanel] = useState("NONE");
    useEffect(()=>{
        /**
     * We use this instead of `useRouter` because we want to avoid re-rendering
     * the component when the query changes.
     */ const searchParams = new URLSearchParams(window.location.search);
        const tab = searchParams.get(QueryParamNames.tab);
        if (tab) {
            setPanel(tab);
        }
    }, []);
    const updatePanel = useCallback((panel)=>{
        setPanel(panel);
        if (panel === "NONE") {
            Router.push({
                pathname: Router.pathname,
                query: omit(Router.query, QueryParamNames.tab)
            }, undefined, {
                shallow: true
            });
        } else {
            Router.push({
                pathname: Router.pathname,
                query: {
                    ...Router.query,
                    [QueryParamNames.tab]: panel.toString()
                }
            }, undefined, {
                shallow: true
            });
        }
    }, []);
    const togglePanel = useCallback((newPanel)=>{
        if (newPanel === panel) {
            updatePanel("NONE");
        } else {
            updatePanel(newPanel);
        }
    }, [
        panel,
        updatePanel
    ]);
    const hidePanel = useCallback(()=>{
        updatePanel("NONE");
    }, [
        updatePanel
    ]);
    const showPanel = useCallback((panel)=>{
        updatePanel(panel);
    }, [
        updatePanel
    ]);
    const value = useMemo(()=>{
        return {
            hidePanel,
            panel,
            showPanel,
            togglePanel,
            updatePanel
        };
    }, [
        hidePanel,
        panel,
        showPanel,
        togglePanel,
        updatePanel
    ]);
    return /*#__PURE__*/ _jsx(AssetModalPanelContext.Provider, {
        value: value,
        children: children
    });
};
export const useAssetModalPanelContext = ()=>{
    const context = useContext(AssetModalPanelContext);
    if (context === defaultContextValue) {
        const error = "useAssetModalPanelContext must be used within an AssetModalPanelContextProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
